import * as Sentry from "@sentry/react";
import type { Breadcrumb, ErrorEvent } from "@sentry/react";
import type { TransactionEvent } from "@sentry/core";

import { SENTRY_ENVIRONMENTS, type SentryEnvironment } from "./sentry.constants";

const appEnv = import.meta.env.VITE_APP_ENV as string | undefined;
const isSupportedEnv = (SENTRY_ENVIRONMENTS as readonly string[]).includes(appEnv ?? "");
const dsn = isSupportedEnv ? (import.meta.env.VITE_SENTRY_DSN as string | undefined) : undefined;

const isSentryEnabled = !!dsn;

const SENSITIVE_URL_QUERY_PARAMS = ["token", "code"];

// SERV-3046: the no-login visit form carries its credential as a path segment, both in the
// page URL and in every API call it makes. Query-param redaction never sees it.
const SECRET_PATH_SEGMENT = /(\/sp-visit-form\/|\/visit\/public\/form-link\/)[^/?#\s]+/g;

// A share link's token is a path segment of the page URL and of every API call the
// shared view makes. It is not a credential on its own, but it should not sit in Sentry.
const SHARE_LINK_PATH_SEGMENT = /(\/files\/(?:s|links)\/)[^/?#\s]+/g;

function isSecretTokenPage(): boolean {
  return window.location.pathname.startsWith("/sp-visit-form/");
}

export function redactSecretPathSegments(text: string): string {
  return text
    .replace(SECRET_PATH_SEGMENT, "$1[Filtered]")
    .replace(SHARE_LINK_PATH_SEGMENT, "$1[token]");
}

export function sanitizeSentryUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  let parsed: URL;
  try {
    parsed = new URL(url, window.location.origin);
  } catch {
    return redactSecretPathSegments(url);
  }
  let redacted = false;
  for (const param of SENSITIVE_URL_QUERY_PARAMS) {
    if (parsed.searchParams.has(param)) {
      parsed.searchParams.set(param, "[Filtered]");
      redacted = true;
    }
  }
  return redactSecretPathSegments(redacted ? parsed.toString() : url);
}

export function sanitizeSentryBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  const data = breadcrumb.data;
  if (!data) return breadcrumb;

  if (typeof data.url === "string") data.url = sanitizeSentryUrl(data.url);
  if (typeof data.to === "string") data.to = sanitizeSentryUrl(data.to);
  if (typeof data.from === "string") data.from = sanitizeSentryUrl(data.from);

  return breadcrumb;
}

export function sanitizeSentryErrorEvent(event: ErrorEvent): ErrorEvent {
  if (event.request?.url) {
    event.request.url = sanitizeSentryUrl(event.request.url);
  }
  // Error events inherit the pageload transaction name, which is the raw pathname.
  if (event.transaction) {
    event.transaction = redactSecretPathSegments(event.transaction);
  }
  return event;
}

export function sanitizeSentryTransactionEvent(event: TransactionEvent): TransactionEvent {
  if (event.request?.url) {
    event.request.url = sanitizeSentryUrl(event.request.url);
  }
  if (event.transaction) {
    event.transaction = redactSecretPathSegments(event.transaction);
  }
  for (const span of event.spans ?? []) {
    if (span.description) span.description = redactSecretPathSegments(span.description);
    for (const [key, value] of Object.entries(span.data ?? {})) {
      if (typeof value === "string") span.data[key] = redactSecretPathSegments(value);
    }
  }
  return event;
}

function getTracePropagationTargets(): (string | RegExp)[] {
  const baseUrl = import.meta.env.VITE_BASE_URL as string | undefined;
  if (!baseUrl) return [];
  try {
    return [new URL(baseUrl).origin];
  } catch {
    return [];
  }
}

if (isSentryEnabled) {
  Sentry.init({
    dsn,
    environment: appEnv as SentryEnvironment,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    sendDefaultPii: true,
    // SERV-3046: no replay on the no-login visit form page. A recording carries the page
    // URL and every request URL, which hold the token, and beforeSend never sees it.
    integrations: [
      Sentry.browserTracingIntegration(),
      ...(isSecretTokenPage()
        ? []
        : [Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true })]),
    ],
    tracesSampleRate: 1,
    tracePropagationTargets: getTracePropagationTargets(),
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1,
    enableLogs: true,
    beforeBreadcrumb: sanitizeSentryBreadcrumb,
    beforeSend: sanitizeSentryErrorEvent,
    beforeSendTransaction: sanitizeSentryTransactionEvent,
  });
}
